// Stable signin page, do not make any further changes to it.

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { createResizeObserver } from '../utils/resizeObserver';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { Turnstile } from '@marsidev/react-turnstile';
import { FcGoogle } from 'react-icons/fc';
import { FaApple, FaFacebook } from 'react-icons/fa';
import { TbArticle } from 'react-icons/tb';
import axios from 'axios';
import { appApiUrl } from '../utils/appApiOrigin';
import { performClientSignOut } from '../utils/sessionSignOut';
import { checkAppSession, markSessionValid, peekSessionCache } from '../utils/sessionAuth';
import CenterModalWindow from '../layout/ReusableComponent/CenterModalWindow/CenterModalWindow';
import { AuthRouteSkeleton } from '../layout/ReusableComponent/Skeleton/RouteSkeletons';

import './signin.css';
import {
  APP_NAME,
  APP_TAGLINE,
  HERO_HEADLINE,
  HERO_DESCRIPTION,
  HERO_HIGHLIGHTS,
  SIGNIN_CARD_SUBTITLE,
  LOGO_SRC,
  getCurrentYear,
} from '../config/branding';
import {
  buildSigninUrl,
  clearFeedScrollState,
  isGuestPostsViewEnabled,
  markPostsFreshAfterLogin,
  postsUrlAfterLogin,
  safePostsReturnTo,
  consumePendingVerificationEmail,
} from '../newsintelligence/support/openDiscussions';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_CLOUDFARE_TURNSTILE_CLIENT_SITE_KEY || '';
const MIN_SIGNUP_AGE_YEARS = 13;

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
  { value: 'other', label: 'Other' },
];

function parseDateOfBirth(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const parsed = new Date(`${raw}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function getMaxBirthDateForSignup() {
  const today = new Date();
  const cutoff = new Date(
    Date.UTC(
      today.getUTCFullYear() - MIN_SIGNUP_AGE_YEARS,
      today.getUTCMonth(),
      today.getUTCDate()
    )
  );
  return cutoff.toISOString().slice(0, 10);
}

function isAtLeastMinAge(dobValue, minYears = MIN_SIGNUP_AGE_YEARS) {
  const dob = typeof dobValue === 'string' ? parseDateOfBirth(dobValue) : dobValue;
  if (!dob) return false;

  const today = new Date();
  let age = today.getUTCFullYear() - dob.getUTCFullYear();
  const monthDiff = today.getUTCMonth() - dob.getUTCMonth();
  const dayDiff = today.getUTCDate() - dob.getUTCDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age -= 1;
  }

  return age >= minYears;
}

const SigninContent = () => {
  const [authMode, setAuthMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [retypePassword, setRetypePassword] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [signupError, setSignupError] = useState('');
  const [authModal, setAuthModal] = useState(null);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotTurnstileToken, setForgotTurnstileToken] = useState('');
  const [isForgotSubmitting, setIsForgotSubmitting] = useState(false);
  const forgotTurnstileRef = useRef(null);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [googleBtnWidth, setGoogleBtnWidth] = useState(0);
  const googleWrapRef = useRef(null);
  const turnstileRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const postsReturnTo = safePostsReturnTo(searchParams.get('returnTo'));
  const skipAutoRedirect = searchParams.get('reauth') === '1';
  const turnstileRequired = Boolean(TURNSTILE_SITE_KEY);
  const maxBirthDate = getMaxBirthDateForSignup();

  const resetTurnstile = useCallback(() => {
    setTurnstileToken('');
    turnstileRef.current?.reset();
  }, []);

  const ensureTurnstileToken = () => {
    if (turnstileRequired && !turnstileToken) {
      setAuthModal({
        type: 'signin-error',
        title: 'Security verification',
        message: 'Please complete the security verification on the sign-in form.',
      });
      return false;
    }
    return true;
  };

  const resetForgotTurnstile = useCallback(() => {
    setForgotTurnstileToken('');
    forgotTurnstileRef.current?.reset();
  }, []);

  const closeAuthModal = useCallback(() => {
    setAuthModal(null);
    resetForgotTurnstile();
  }, [resetForgotTurnstile]);

  const openSigninErrorModal = useCallback((message, options = {}) => {
    setAuthModal({
      type: options.emailNotVerified ? 'email-not-verified' : 'signin-error',
      title: options.title || 'Sign in failed',
      message,
      emailNotVerified: !!options.emailNotVerified,
    });
  }, []);

  useEffect(() => {
    document.body.classList.add('signin-page-active');
    return () => document.body.classList.remove('signin-page-active');
  }, []);

  useEffect(() => {
    if (searchParams.get('mode') === 'signup') {
      setAuthMode('signup');
    }
  }, [searchParams]);

  useEffect(() => {
    const pendingEmail = consumePendingVerificationEmail();
    if (!pendingEmail) return;
    setAuthMode('signin');
    setEmail(pendingEmail);
    setEmailNotVerified(true);
    setAuthModal({
      type: 'email-not-verified',
      title: 'Verify your email',
      message:
        'Please verify your email before signing in. Check your inbox for the verification link, or resend a new verification email.',
      emailNotVerified: true,
    });
  }, []);

  const redirectToPostsAfterLogin = useCallback(() => {
    clearFeedScrollState();
    markPostsFreshAfterLogin();
    navigate(postsUrlAfterLogin(postsReturnTo));
  }, [navigate, postsReturnTo]);

  useEffect(() => {
    if (!skipAutoRedirect) return undefined;
    let cancelled = false;
    (async () => {
      await performClientSignOut();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [skipAutoRedirect]);

  useLayoutEffect(() => {
    if (skipAutoRedirect) return;
    if (peekSessionCache()) {
      markSessionValid();
      redirectToPostsAfterLogin();
    }
  }, [skipAutoRedirect, redirectToPostsAfterLogin]);

  useEffect(() => {
    if (skipAutoRedirect) return undefined;

    const redirectIfAlreadySignedIn = async () => {
      try {
        if (peekSessionCache()) {
          markSessionValid();
          redirectToPostsAfterLogin();
          return;
        }
        const ok = await checkAppSession({ force: true });
        if (ok) {
          markSessionValid();
          redirectToPostsAfterLogin();
        }
      } catch (err) {
        console.error('Error validating session:', err);
      } finally {
        setLoading(false);
      }
    };

    redirectIfAlreadySignedIn();
    return undefined;
  }, [redirectToPostsAfterLogin, skipAutoRedirect]);

  useEffect(() => {
    const syncTheme = () => {
      const theme = document.documentElement.getAttribute('data-theme');
      setIsDarkTheme(theme === 'dark');
    };

    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = googleWrapRef.current;
    if (!el) return undefined;

    const updateWidth = () => {
      const next = Math.floor(el.getBoundingClientRect().width);
      if (next > 0) {
        setGoogleBtnWidth((prev) => (prev === next ? prev : next));
      }
    };

    updateWidth();
    const observer = createResizeObserver(updateWidth);
    if (observer) {
      observer.observe(el);
      return () => observer.disconnect();
    }
    return undefined;
  }, [loading, authMode]);

  const completeLogin = () => {
    markSessionValid();
    redirectToPostsAfterLogin();
  };

  const handleResendVerification = async () => {
    const resendEmail = email.trim();
    if (!resendEmail) {
      openSigninErrorModal('Enter your email address on the sign-in form to resend the verification link.');
      return;
    }

    const modalTurnstile = forgotTurnstileToken || turnstileToken;
    if (turnstileRequired && !modalTurnstile) {
      setAuthModal((prev) => ({
        ...(prev || { type: 'email-not-verified', title: 'Verify your email', message: '' }),
        type: 'email-not-verified',
        title: 'Verify your email',
        message: prev?.message || 'Please verify your email before signing in.',
        emailNotVerified: true,
        turnstileError: 'Please complete the security verification below.',
      }));
      return;
    }

    setIsResendingVerification(true);
    try {
      const response = await axios.post(
        appApiUrl('/api/resend-verification-email'),
        { email: resendEmail, turnstileToken: modalTurnstile },
        { withCredentials: true }
      );

      if (response.data.success) {
        setEmailNotVerified(false);
        resetTurnstile();
        resetForgotTurnstile();
        setAuthModal({
          type: 'account-created',
          title: 'Verification email sent',
          message: response.data.message || 'A new verification email has been sent. Please check your inbox.',
        });
      } else {
        openSigninErrorModal(response.data.message || 'Unable to resend verification email.', {
          emailNotVerified: true,
          title: 'Verify your email',
        });
        resetTurnstile();
        resetForgotTurnstile();
      }
    } catch (err) {
      openSigninErrorModal(err.response?.data?.message || 'Unable to resend verification email.', {
        emailNotVerified: true,
        title: 'Verify your email',
      });
      resetTurnstile();
      resetForgotTurnstile();
    } finally {
      setIsResendingVerification(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();

    if (!forgotEmail.trim()) {
      setAuthModal((prev) => ({
        ...prev,
        forgotError: 'Email is required.',
      }));
      return;
    }

    if (turnstileRequired && !forgotTurnstileToken) {
      setAuthModal((prev) => ({
        ...prev,
        forgotError: 'Please complete the security verification.',
      }));
      return;
    }

    setIsForgotSubmitting(true);
    try {
      const response = await axios.post(
        appApiUrl('/api/forgot-password'),
        { email: forgotEmail.trim(), turnstileToken: forgotTurnstileToken },
        { withCredentials: true }
      );

      if (response.data.success) {
        resetForgotTurnstile();
        setAuthModal({
          type: 'forgot-sent',
          title: 'Check your email',
          message: response.data.message || 'If an account exists for this email, a password reset link has been sent.',
        });
      } else {
        setAuthModal((prev) => ({
          ...prev,
          forgotError: response.data.message || 'Unable to send reset email.',
        }));
        resetForgotTurnstile();
      }
    } catch (err) {
      setAuthModal((prev) => ({
        ...prev,
        forgotError: err.response?.data?.message || 'Unable to send reset email right now.',
      }));
      resetForgotTurnstile();
    } finally {
      setIsForgotSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSignupError('');
    setEmailNotVerified(false);

    if (!ensureTurnstileToken()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(
        appApiUrl('/api/signin'),
        { email, password, turnstileToken },
        { withCredentials: true }
      );

      if (response.data.success) {
        completeLogin();
      } else {
        openSigninErrorModal(response.data.message || 'Login failed, try again.');
        resetTurnstile();
      }
    } catch (err) {
      console.error('Error during login:', err);
      const responseData = err.response?.data;
      const notVerified = responseData?.code === 'EMAIL_NOT_VERIFIED';
      setEmailNotVerified(notVerified);
      openSigninErrorModal(responseData?.message || 'Login failed, try again.', {
        emailNotVerified: notVerified,
        title: notVerified ? 'Verify your email' : 'Sign in failed',
      });
      resetTurnstile();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setSignupError('');
    setEmailNotVerified(false);

    if (signupPassword !== retypePassword) {
      setSignupError('Passwords do not match. Please retype your password.');
      return;
    }

    if (!dateOfBirth) {
      setSignupError('Date of birth is required.');
      return;
    }

    if (!isAtLeastMinAge(dateOfBirth)) {
      setSignupError(`You must be at least ${MIN_SIGNUP_AGE_YEARS} years old to create an account.`);
      return;
    }

    if (!gender) {
      setSignupError('Please select a gender.');
      return;
    }

    if (!termsAccepted) {
      setSignupError('Please agree to the Terms and Conditions and Privacy Policy to continue.');
      return;
    }

    if (!ensureTurnstileToken()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        appApiUrl('/api/signup'),
        {
          email: signupEmail,
          password: signupPassword,
          firstName,
          lastName,
          dateOfBirth,
          gender,
          turnstileToken,
          termsAccepted: true,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        setAuthMode('signin');
        setEmail(signupEmail);
        setPassword('');
        setAuthModal({
          type: 'account-created',
          title: 'Account created',
          message:
            response.data.message
            || 'Account created. Please check your email and click the verification link before signing in.',
        });
        navigate(buildSigninUrl({ mode: 'signin', returnTo: postsReturnTo }), { replace: true });
        resetTurnstile();
      } else {
        setSignupError(response.data.message || 'Unable to create account. Please try again.');
        resetTurnstile();
      }
    } catch (err) {
      console.error('Error during signup:', err);
      setSignupError(err.response?.data?.message || 'Unable to create account right now. Please try again.');
      resetTurnstile();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setSignupError('');
    setIsGoogleLoading(true);

    try {
      const response = await axios.post(
        appApiUrl('/api/auth/google'),
        { credential: credentialResponse.credential },
        { withCredentials: true }
      );

      if (response.data.success) {
        completeLogin();
      } else {
        openSigninErrorModal(response.data.message || 'Google sign-in failed.', { title: 'Google sign-in failed' });
      }
    } catch (err) {
      console.error('Error during Google login:', err);
      openSigninErrorModal(err.response?.data?.message || 'Google sign-in failed. Please try again.', {
        title: 'Google sign-in failed',
      });
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleLogo = () => navigate('/');

  const switchAuthMode = (nextMode) => {
    setAuthMode(nextMode);
    setSignupError('');
    setEmailNotVerified(false);
    resetTurnstile();
    navigate(buildSigninUrl({ mode: nextMode, returnTo: postsReturnTo }), { replace: true });
  };

  const renderTurnstile = (panelKey) => {
    if (!TURNSTILE_SITE_KEY) return null;

    return (
      <div className="signin-turnstile" key={panelKey}>
        <Turnstile
          ref={turnstileRef}
          siteKey={TURNSTILE_SITE_KEY}
          onSuccess={setTurnstileToken}
          onExpire={() => setTurnstileToken('')}
          onError={() => {
            setTurnstileToken('');
            openSigninErrorModal('Security verification failed. Please try again.', {
              title: 'Security verification',
            });
          }}
          options={{ theme: isDarkTheme ? 'dark' : 'light' }}
        />
      </div>
    );
  };

  if (loading) {
    if (
      postsReturnTo.startsWith('/storagebox') ||
      postsReturnTo.startsWith('/costing') ||
      postsReturnTo.startsWith('/sm/all-messages') ||
      postsReturnTo.startsWith('/messages')
    ) {
      return <AuthRouteSkeleton />;
    }
    return (
      <div className="signin-page signin-page--loading" aria-busy="true">
        <div className="signin-spinner" aria-label="Loading" />
      </div>
    );
  }

  const socialBusy = isGoogleLoading || isSubmitting;
  const isSignupMode = authMode === 'signup';
  const authBusy = isSubmitting;
  const signinSubmitDisabled = socialBusy || (turnstileRequired && !turnstileToken);
  const signupSubmitDisabled = authBusy || (turnstileRequired && !turnstileToken);

  return (
    <div className="signin-page">
      <header className="signin-topbar">
        <button type="button" className="signin-logo-btn" onClick={handleLogo} aria-label="Go to home">
          <img
            src={LOGO_SRC}
            alt={APP_NAME}
            className="signin-logo-img"
          />
        </button>
        {isGuestPostsViewEnabled() ? (
          <Link to={postsReturnTo} className="signin-view-posts">
            <TbArticle className="signin-view-posts-icon" aria-hidden="true" />
            Explore posts
          </Link>
        ) : null}
      </header>

      <main className="signin-main">
        <section className="signin-hero">
          <div className="signin-hero-scroll">
          <div className="signin-hero-content">
            <p className="signin-hero-eyebrow">{APP_TAGLINE}</p>
            <h1 className="signin-hero-title">{HERO_HEADLINE}</h1>
            <p className="signin-hero-text">{HERO_DESCRIPTION}</p>
            <div className="signin-theme-pillars" aria-label="Meet Share Grow">
              <span>Meet</span>
              <span>Share</span>
              <span>Grow</span>
            </div>
            <ul className="signin-hero-highlights">
              {HERO_HIGHLIGHTS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="signin-community-collage">
            <div className="signin-collage-card signin-collage-card--discussions">
              <span className="signin-collage-emoji" aria-hidden="true">💬</span>
              <h3>Discussions</h3>
              <p>Ask questions, solve together, and keep ideas flowing.</p>
            </div>

            <div className="signin-collage-card signin-collage-card--communities">
              <span className="signin-collage-emoji" aria-hidden="true">🤝</span>
              <h3>Communities</h3>
              <p>Join groups that match your interests and projects.</p>
            </div>

            <div className="signin-collage-card signin-collage-card--comments">
              <span className="signin-collage-emoji" aria-hidden="true">✨</span>
              <h3>Comments</h3>
              <p>Give feedback, react quickly, and celebrate progress.</p>
            </div>

            <span className="signin-float-badge signin-float-badge--one" aria-hidden="true">😀</span>
            <span className="signin-float-badge signin-float-badge--two" aria-hidden="true">🚀</span>
            <span className="signin-float-badge signin-float-badge--three" aria-hidden="true">❤️</span>
          </div>
          </div>
        </section>

        <section className="signin-panel">
          <div className="signin-card">
            <div className="signin-card-header">
              <div
                className={`signin-card-header-panel${!isSignupMode ? ' signin-card-header-panel--active' : ''}`}
                aria-hidden={isSignupMode}
                inert={isSignupMode || undefined}
              >
                <h2>Welcome</h2>
                <p>{SIGNIN_CARD_SUBTITLE}</p>
              </div>
              <div
                className={`signin-card-header-panel${isSignupMode ? ' signin-card-header-panel--active' : ''}`}
                aria-hidden={!isSignupMode}
                inert={!isSignupMode || undefined}
              >
                <h2>Create account</h2>
                <p>Join now and get started with your workspace.</p>
              </div>
            </div>

            <div className="signin-auth-toggle" role="tablist" aria-label="Authentication mode">
              <button
                type="button"
                role="tab"
                aria-selected={!isSignupMode}
                className={`signin-auth-tab ${!isSignupMode ? 'signin-auth-tab--active' : ''}`}
                onClick={() => {
                  setAuthMode('signin');
                  setSignupError('');
                  resetTurnstile();
                }}
                disabled={authBusy}
              >
                Sign in
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isSignupMode}
                className={`signin-auth-tab ${isSignupMode ? 'signin-auth-tab--active' : ''}`}
                onClick={() => {
                  setAuthMode('signup');
                  setSignupError('');
                  resetTurnstile();
                }}
                disabled={authBusy}
              >
                Create account
              </button>
            </div>

            <div className="signin-card-body">
              <div
                className={`signin-card-panel${!isSignupMode ? ' signin-card-panel--active' : ''}`}
                aria-hidden={isSignupMode}
                inert={isSignupMode || undefined}
              >
                <div className="signin-social">
                  {/* Google is disabled for now. */}
                  {/* {GOOGLE_CLIENT_ID ? (
                    <div
                      ref={googleWrapRef}
                      className={`signin-google-wrap ${socialBusy ? 'signin-google-wrap--busy' : ''}`}
                    >
                      {googleBtnWidth > 0 && (
                        <GoogleLogin
                          onSuccess={handleGoogleSuccess}
                          onError={() => openSigninErrorModal('Google sign-in was cancelled or failed.', { title: 'Google sign-in failed' })}
                          theme={isDarkTheme ? 'filled_black' : 'outline'}
                          size="large"
                          text="continue_with"
                          shape="rectangular"
                          width={googleBtnWidth}
                          logo_alignment="left"
                        />
                      )}
                    </div>
                  ) : (
                    <button type="button" className="signin-social-btn signin-social-btn--google" disabled>
                      <FcGoogle className="signin-social-icon" aria-hidden="true" />
                      Continue with Google
                    </button>
                  )} */}

                  {/* Apple is disabled for now. */}
                  {/* <button
                    type="button"
                    className="signin-social-btn signin-social-btn--disabled"
                    disabled
                    title="Coming soon"
                  >
                    <FaApple className="signin-social-icon" aria-hidden="true" />
                    Continue with Apple
                    <span className="signin-soon-badge">Soon</span>
                  </button> */}

                  {/* Facebook is disabled for now. */}
                  {/* <button
                    type="button"
                    className="signin-social-btn signin-social-btn--disabled"
                    disabled
                    title="Coming soon"
                  >
                    <FaFacebook className="signin-social-icon signin-social-icon--facebook" aria-hidden="true" />
                    Continue with Facebook
                    <span className="signin-soon-badge">Soon</span>
                  </button>*/}

                </div> 

                <div className="signin-divider">
                  <span>sign in with email</span>
                </div>

                <form className="signin-form" onSubmit={handleSubmit}>
                  <label className="signin-field">
                    <span className="signin-field-label">Email</span>
                    <input
                      type="email"
                      placeholder="Enter your email"
                      className="signin-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      required
                      disabled={socialBusy}
                      tabIndex={isSignupMode ? -1 : 0}
                    />
                  </label>

                  <label className="signin-field">
                    <span className="signin-field-label">Password</span>
                    <input
                      type="password"
                      placeholder="Enter your password"
                      className="signin-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                      disabled={socialBusy}
                      tabIndex={isSignupMode ? -1 : 0}
                    />
                  </label>

                  <button
                    type="button"
                    className="signin-forgot"
                    onClick={() => {
                      setForgotEmail(email);
                      resetForgotTurnstile();
                      setAuthModal({ type: 'forgot', title: 'Reset password', message: '' });
                    }}
                    tabIndex={isSignupMode ? -1 : 0}
                  >
                    Forgot password?
                  </button>

                  {!isSignupMode ? renderTurnstile('signin') : null}

                  <button type="submit" className="signin-submit" disabled={signinSubmitDisabled} tabIndex={isSignupMode ? -1 : 0}>
                    {isSubmitting ? 'Signing in…' : 'Sign in'}
                  </button>
                </form>

                <p className="signin-join">
                  New member?{' '}
                  <button
                    type="button"
                    className="signin-link"
                    onClick={() => switchAuthMode('signup')}
                    tabIndex={isSignupMode ? -1 : 0}
                  >
                    Join now
                  </button>
                </p>
              </div>

              <div
                className={`signin-card-panel${isSignupMode ? ' signin-card-panel--active' : ''}`}
                aria-hidden={!isSignupMode}
                inert={!isSignupMode || undefined}
              >
                <form className="signin-form signin-form--signup" onSubmit={handleCreateAccount}>
                  <div className="signin-form-grid signin-form-grid--names">
                    <label className="signin-field">
                      <span className="signin-field-label">First name</span>
                      <input
                        type="text"
                        placeholder="First name"
                        className="signin-input"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        autoComplete="given-name"
                        required
                        disabled={authBusy}
                        tabIndex={!isSignupMode ? -1 : 0}
                      />
                    </label>

                    <label className="signin-field">
                      <span className="signin-field-label">Last name</span>
                      <input
                        type="text"
                        placeholder="Last name"
                        className="signin-input"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        autoComplete="family-name"
                        required
                        disabled={authBusy}
                        tabIndex={!isSignupMode ? -1 : 0}
                      />
                    </label>
                  </div>

                  <div className="signin-form-grid signin-form-grid--profile">
                    <label className="signin-field">
                      <span className="signin-field-label">Date of birth</span>
                      <input
                        type="date"
                        className="signin-input"
                        value={dateOfBirth}
                        onChange={(e) => setDateOfBirth(e.target.value)}
                        max={maxBirthDate}
                        autoComplete="bday"
                        required
                        disabled={authBusy}
                        tabIndex={!isSignupMode ? -1 : 0}
                        aria-describedby="signin-dob-hint"
                      />
                      <span id="signin-dob-hint" className="signin-field-hint">
                        You must be at least {MIN_SIGNUP_AGE_YEARS} years old to create an account.
                      </span>
                    </label>

                    <label className="signin-field">
                      <span className="signin-field-label">Gender</span>
                      <select
                        className="signin-input signin-select"
                        value={gender}
                        onChange={(e) => setGender(e.target.value)}
                        required
                        disabled={authBusy}
                        tabIndex={!isSignupMode ? -1 : 0}
                      >
                        <option value="">Select gender</option>
                        {GENDER_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <label className="signin-field">
                    <span className="signin-field-label">Email</span>
                    <input
                      type="email"
                      placeholder="Enter your email"
                      className="signin-input"
                      value={signupEmail}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      autoComplete="email"
                      required
                      disabled={authBusy}
                      tabIndex={!isSignupMode ? -1 : 0}
                    />
                  </label>

                  <label className="signin-field">
                    <span className="signin-field-label">Password</span>
                    <input
                      type="password"
                      placeholder="Create a password"
                      className="signin-input"
                      value={signupPassword}
                      onChange={(e) => setSignupPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                      disabled={authBusy}
                      tabIndex={!isSignupMode ? -1 : 0}
                    />
                  </label>

                  <label className="signin-field">
                    <span className="signin-field-label">Retype password</span>
                    <input
                      type="password"
                      placeholder="Retype your password"
                      className="signin-input"
                      value={retypePassword}
                      onChange={(e) => setRetypePassword(e.target.value)}
                      autoComplete="new-password"
                      required
                      disabled={authBusy}
                      tabIndex={!isSignupMode ? -1 : 0}
                    />
                  </label>

                  <label className="signin-terms">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      disabled={authBusy}
                      tabIndex={!isSignupMode ? -1 : 0}
                    />
                    <span>
                      I agree to the{' '}
                      <Link to="/terms" className="signin-link" tabIndex={!isSignupMode ? -1 : 0}>
                        Terms and Conditions
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy" className="signin-link" tabIndex={!isSignupMode ? -1 : 0}>
                        Privacy Policy
                      </Link>
                    </span>
                  </label>

                  {isSignupMode ? renderTurnstile('signup') : null}

                  <button type="submit" className="signin-submit" disabled={signupSubmitDisabled} tabIndex={!isSignupMode ? -1 : 0}>
                    {isSubmitting ? 'Creating account…' : 'Create account'}
                  </button>
                </form>

                <p className="signin-join">
                  Already have an account?{' '}
                  <button
                    type="button"
                    className="signin-link"
                    onClick={() => switchAuthMode('signin')}
                    tabIndex={!isSignupMode ? -1 : 0}
                  >
                    Sign in
                  </button>
                </p>
              </div>
            </div>

            {signupError && isSignupMode ? (
              <div className="signin-error" role="alert">
                {signupError}
              </div>
            ) : null}
          </div>
        </section>
      </main>

      <CenterModalWindow
        isOpen={Boolean(authModal)}
        onClose={closeAuthModal}
        width="min(440px, 94vw)"
        height="auto"
        contentClassName="signin-auth-modal"
      >
        {authModal ? (
          <div className="signin-auth-modal-body">
            <img src={LOGO_SRC} alt="" className="signin-auth-modal-logo" aria-hidden="true" />
            <p className="signin-auth-modal-eyebrow">{APP_TAGLINE}</p>
            <h2 className="signin-auth-modal-title">{authModal.title}</h2>

            {authModal.type === 'forgot' ? (
              <form className="signin-auth-modal-form" onSubmit={handleForgotPasswordSubmit}>
                <p className="signin-auth-modal-text">
                  Enter the email address for your account. We will send a reset link if it exists.
                </p>
                <label className="signin-field">
                  <span className="signin-field-label">Email</span>
                  <input
                    type="email"
                    className="signin-input"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    autoComplete="email"
                    required
                    disabled={isForgotSubmitting}
                  />
                </label>
                {TURNSTILE_SITE_KEY ? (
                  <div className="signin-turnstile">
                    <Turnstile
                      ref={forgotTurnstileRef}
                      siteKey={TURNSTILE_SITE_KEY}
                      onSuccess={setForgotTurnstileToken}
                      onExpire={() => setForgotTurnstileToken('')}
                      options={{ theme: isDarkTheme ? 'dark' : 'light' }}
                    />
                  </div>
                ) : null}
                {authModal.forgotError ? (
                  <div className="signin-auth-modal-message signin-auth-modal-message--error" role="alert">
                    {authModal.forgotError}
                  </div>
                ) : null}
                <button
                  type="submit"
                  className="signin-submit signin-auth-modal-submit"
                  disabled={isForgotSubmitting}
                >
                  {isForgotSubmitting ? 'Sending…' : 'Send reset link'}
                </button>
              </form>
            ) : (
              <>
                {authModal.message ? (
                  <div
                    className={`signin-auth-modal-message ${
                      authModal.type === 'account-created' || authModal.type === 'forgot-sent'
                        ? 'signin-auth-modal-message--success'
                        : 'signin-auth-modal-message--error'
                    }`}
                    role={authModal.type === 'signin-error' || authModal.type === 'email-not-verified' ? 'alert' : 'status'}
                  >
                    {authModal.message}
                  </div>
                ) : null}

                {authModal.turnstileError ? (
                  <div className="signin-auth-modal-message signin-auth-modal-message--error" role="alert">
                    {authModal.turnstileError}
                  </div>
                ) : null}

                {(authModal.type === 'email-not-verified' || authModal.emailNotVerified) ? (
                  <>
                    {TURNSTILE_SITE_KEY ? (
                      <div className="signin-turnstile">
                        <Turnstile
                          ref={forgotTurnstileRef}
                          siteKey={TURNSTILE_SITE_KEY}
                          onSuccess={setForgotTurnstileToken}
                          onExpire={() => setForgotTurnstileToken('')}
                          options={{ theme: isDarkTheme ? 'dark' : 'light' }}
                        />
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="signin-submit signin-auth-modal-submit"
                      onClick={handleResendVerification}
                      disabled={isResendingVerification || isSubmitting}
                    >
                      {isResendingVerification ? 'Sending verification email…' : 'Resend verification email'}
                    </button>
                  </>
                ) : null}

                {authModal.type === 'account-created' || authModal.type === 'forgot-sent' ? (
                  <button
                    type="button"
                    className="signin-submit signin-auth-modal-submit"
                    onClick={closeAuthModal}
                  >
                    Continue to sign in
                  </button>
                ) : authModal.type === 'signin-error' ? (
                  <button
                    type="button"
                    className="signin-submit signin-auth-modal-submit"
                    onClick={closeAuthModal}
                  >
                    Try again
                  </button>
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </CenterModalWindow>

      <footer className="signin-footer">
        <span>© {getCurrentYear()} {APP_NAME}</span>
        <Link to="/privacy" className="signin-footer-link">Privacy</Link>
        <Link to="/docs" className="signin-footer-link">Documentation</Link>
        <Link to="/contact" className="signin-footer-link">Contact</Link>
      </footer>
    </div>
  );
};

const Signin = () => {
  if (!GOOGLE_CLIENT_ID) {
    return <SigninContent />;
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <SigninContent />
    </GoogleOAuthProvider>
  );
};

export default Signin;
